"use client";
import { motion } from "framer-motion";
import { Eye, EyeOff, Lock, Mail } from "lucide-react";
import Link from "next/link";
import { redirect } from "next/navigation";
import { useTranslations } from "next-intl";
import { useTheme } from "next-themes";
import { useActionState, useEffect, useState } from "react";
import { loginAction } from "@/actions/auth";
import { StatsGrid } from "@/components/login/stats-grid";
import { TestimonialCarousel } from "@/components/login/testimonial-carousel";
import EkomLogo from "@/components/logo";
import { SimpleThemeToggle } from "@/components/theme-switcher";
import { BackgroundGradientAnimation } from "@/components/ui/background-gradient-animation";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { toast } from "@/components/ui/use-toast";
import { parseUIMode } from "@/lib/utils";
import type { UIMode } from "@/types/user";

export default function Login() {
  const t = useTranslations();

  const testimonials = t.raw("login.testimonials");
  const { setTheme } = useTheme();
  const [state, formAction, isLoading] = useActionState(loginAction, null);
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    if (state?.error) {
      toast({
        title: t("notifications.error"),
        description: state.error,
        variant: "destructive",
      });
    } else if (state?.success) {
      setTheme(parseUIMode(state.user?.ui_mode as UIMode));
      redirect("/app/validating/dashboard");
    }
  }, [state, t, setTheme]);

  return (
    <BackgroundGradientAnimation>
      <div className="absolute top-4 right-4 md:top-6 md:right-6 z-20">
        <SimpleThemeToggle />
      </div>

      <motion.div
        initial={{ opacity: 0, scale: 1.4 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{
          duration: 0.4,
          ease: "easeOut",
        }}
      >
        <div className="relative z-10 flex min-h-screen items-center justify-center px-6 md:px-8 py-8">
          <div className="w-full max-w-[calc(100%-2rem)] md:max-w-3xl lg:max-w-6xl max-h-[90vh] rounded-xl lg:rounded-3xl overflow-hidden flex flex-col lg:flex-row lg:shadow-2xl border border-primary/20 shadow-xl">
            <div className="hidden lg:flex lg:w-2/5 bg-primary p-12 flex-col justify-between">
              <div>
                <div className="flex items-center gap-3 mb-10">
                  <EkomLogo size={32} color="white" />
                  <span className="text-2xl font-bold text-white">EKOM</span>
                </div>
                <div className="flex flex-col gap-2">
                  <StatsGrid />
                  <TestimonialCarousel
                    testimonials={testimonials}
                    customersTitle={t("login.customersTitle")}
                  />
                </div>
              </div>
            </div>

            <div className="flex-1 flex items-center justify-center p-6 md:p-8 lg:w-1/2 bg-card/80 overflow-y-auto">
              <div className="w-full max-w-sm md:max-w-md">
                <div className="mb-8 md:mb-10">
                  <h1 className="text-2xl md:text-3xl font-bold tracking-tight mb-3 text-left">
                    {t("login.title")}
                  </h1>
                  <p className="text-muted-foreground text-sm md:text-base text-left">
                    {t("login.subtitle")}
                  </p>
                </div>

                <form className="space-y-4" action={formAction}>
                  <div className="space-y-2">
                    <label
                      htmlFor="email"
                      className="text-sm font-medium text-foreground mb-1"
                    >
                      {t("login.emailAddress")}
                    </label>
                    <div className="relative group">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 md:w-5 md:h-5 text-muted-foreground group-focus-within:text-primary transition-colors pointer-events-none" />
                      <Input
                        id="email"
                        type="email"
                        placeholder={t("login.emailPlaceholder")}
                        className="pl-10 md:pl-11 h-11 bg-input/30 border border-border focus-visible:!border-primary focus-visible:!ring-primary/20"
                        name="email"
                        required
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label
                      htmlFor="password"
                      className="text-sm font-medium text-foreground"
                    >
                      {t("login.password")}
                    </label>
                    <div className="relative group">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 md:w-5 md:h-5 text-muted-foreground group-focus-within:text-primary transition-colors pointer-events-none" />
                      <Input
                        id="password"
                        type={showPassword ? "text" : "password"}
                        placeholder={t("login.passwordPlaceholder")}
                        className="pl-10 md:pl-11 pr-10 md:pr-11 h-11 bg-input/30 border border-border focus-visible:!border-primary focus-visible:!ring-primary/20"
                        name="password"
                        required
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-primary transition-colors"
                      >
                        {showPassword ? (
                          <EyeOff className="w-4 h-4 md:w-5 md:h-5" />
                        ) : (
                          <Eye className="w-4 h-4 md:w-5 md:h-5" />
                        )}
                      </button>
                    </div>
                  </div>

                  <div className="flex flex-col md:flex-row md:items-center md:justify-between">
                    <div className="flex items-center gap-2">
                      <Checkbox
                        id="remember"
                        className="bg-background border border-border"
                      />
                      <label
                        htmlFor="remember"
                        className="text-sm text-muted-foreground cursor-pointer"
                      >
                        {t("login.rememberMe")}
                      </label>
                    </div>
                    <Link
                      href="/forgot-password"
                      className="text-sm text-primary hover:underline font-medium text-left md:text-right"
                    >
                      {t("login.forgotPassword")}
                    </Link>
                  </div>

                  <Button
                    type="submit"
                    disabled={isLoading}
                    className="w-full h-11 md:h-12 bg-primary hover:bg-primary/90 text-primary-foreground font-semibold text-sm md:text-base"
                  >
                    {isLoading ? t("login.signingIn") : t("login.signIn")}
                  </Button>
                </form>

                <div className="mt-6 md:mt-8 text-center">
                  <span className="text-sm text-muted-foreground">
                    {t("login.noAccount")}{" "}
                  </span>
                  <Link
                    href="/sign-up"
                    className="text-sm text-primary hover:underline font-semibold"
                  >
                    {t("login.signUpFree")}
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </motion.div>
    </BackgroundGradientAnimation>
  );
}
